import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingDown, ArrowRight } from 'lucide-react';
import { useCountUp } from '@/hooks/useCountUp';
import { t, getLocale } from '@/i18n';

// Cena Kč/MWh pro typ tarifu (median pásma).
const tariffPrice = {
  negative: -500,
  offpeak: 1000,
  standard: 1600,
  peak: 2200,
};

// Měrná spotřeba mletí — typická hodnota pro GCC, kWh/t
// (literatura: 25–50 kWh/t podle hrubosti).
const SPECIFIC_ENERGY_KWH_PER_T = 35;

/**
 * Spočítá týdenní úsporu pokud by se část peak slotů přesunula do offpeak/negative.
 *
 * Předpoklady (transparentní pro Petra):
 *  - Měrná spotřeba mletí 35 kWh/t (literatura, GCC).
 *  - Optimalizace přesune polovinu peak slotů do nižšího pásma (mix offpeak + negative).
 *  - Realistický rozsah úspory 5–10 % z týdenního účtu.
 */
export function SavingsCounter({ planningData }) {
  const result = useMemo(() => {
    const slots = planningData.lines.flatMap(l => l.schedule).filter(s => s.material && s.energyTariff);

    let baselineCost = 0;
    let optimisedCost = 0;
    let peakTons = 0;

    for (const slot of slots) {
      const tons = slot.plannedOutput || 0;
      const mwh = (tons * SPECIFIC_ENERGY_KWH_PER_T) / 1000;
      const price = tariffPrice[slot.energyTariff] ?? 1600;
      baselineCost += mwh * price;

      if (slot.energyTariff === 'peak') {
        peakTons += tons;
        // 50 % peak tonáže přesuneme: 30 % do offpeak, 20 % do negative.
        const moveOffpeak = mwh * 0.3;
        const moveNegative = mwh * 0.2;
        const stayPeak = mwh * 0.5;
        optimisedCost += stayPeak * price + moveOffpeak * tariffPrice.offpeak + moveNegative * tariffPrice.negative;
      } else {
        optimisedCost += mwh * price;
      }
    }

    const weekly = Math.round(baselineCost - optimisedCost);
    const annual = weekly * 50; // 50 týdnů
    const savingPct = baselineCost > 0 ? Math.round(((baselineCost - optimisedCost) / baselineCost) * 1000) / 10 : 0;

    return {
      weekly,
      annual,
      savingPct,
      peakTons,
      baseline: Math.round(baselineCost),
    };
  }, [planningData]);

  const animatedAnnual = useCountUp(result.annual, 1200);

  return (
    <Card className="bg-gradient-to-br from-emerald-50/80 to-blue-50/60 backdrop-blur-sm border-emerald-200/60 shadow-elevated">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-md bg-emerald-100">
            <TrendingDown className="h-3.5 w-3.5 text-emerald-700" />
          </div>
          <CardTitle className="text-sm">{t('plan.annualSavingsPotential')}</CardTitle>
        </div>
        <p className="text-[10px] text-muted-foreground mt-0.5 leading-tight">
          {t('plan.savingsAssumption')}
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <div className="text-2xl font-bold tabular-nums text-emerald-700 leading-none">
            {animatedAnnual.toLocaleString(getLocale())} <span className="text-sm font-normal text-emerald-600">{t('plan.czkPerYear')}</span>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1 tabular-nums">
            ≈ {result.weekly.toLocaleString(getLocale())} {t('plan.czkPerWeek')} • {result.savingPct} {t('plan.pctOfPowerBill')}
          </div>
        </div>

        <div className="text-[10px] space-y-0.5 text-muted-foreground border-t border-emerald-200/50 pt-2">
          <div className="flex justify-between">
            <span>{t('plan.specificEnergy')}</span>
            <span className="tabular-nums">{SPECIFIC_ENERGY_KWH_PER_T} kWh/t</span>
          </div>
          <div className="flex justify-between">
            <span>{t('plan.peakTonnageWeek')}</span>
            <span className="tabular-nums">{result.peakTons.toLocaleString(getLocale())} t</span>
          </div>
          <div className="flex justify-between">
            <span>{t('plan.baselineNoOpt')}</span>
            <span className="tabular-nums">{result.baseline.toLocaleString(getLocale())} {t('plan.czk')}</span>
          </div>
        </div>

        <div className="flex items-center gap-1.5 text-[10px] text-emerald-700 bg-white/60 rounded-md px-2 py-1.5">
          <ArrowRight className="h-3 w-3 shrink-0" />
          <span className="leading-tight">{t('plan.assumptionsTuning')}</span>
        </div>
      </CardContent>
    </Card>
  );
}
